import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Criterion, CriteriaGroup } from '@/types/evaluation.types';
import { Importance } from '@/types/evaluation.types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Edit,
  Info,
  Minus,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { CriterionForm } from './CriterionForm';
import { BulkCriterionEntry } from './BulkCriterionEntry';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CriteriaGroupCardProps {
  group: CriteriaGroup;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdateGroup: (group: CriteriaGroup) => void;
  onDeleteGroup: () => void;
  onEditGroup: () => void;
}

const importanceIcons = {
  [Importance.Critical]: <Star className="h-3 w-3 text-red-600" aria-hidden="true" />,
  [Importance.High]: <AlertTriangle className="h-3 w-3 text-orange-600" aria-hidden="true" />,
  [Importance.Medium]: <AlertCircle className="h-3 w-3 text-yellow-600" aria-hidden="true" />,
  [Importance.Low]: <Info className="h-3 w-3 text-blue-600" aria-hidden="true" />,
  [Importance.None]: <Minus className="h-3 w-3 text-gray-400" aria-hidden="true" />,
};

export function CriteriaGroupCard({
  group,
  isExpanded,
  onToggleExpand,
  onUpdateGroup,
  onDeleteGroup,
  onEditGroup,
}: CriteriaGroupCardProps) {
  const { t } = useTranslation(['criteria', 'common']);
  const [criterionFormOpen, setCriterionFormOpen] = useState(false);
  const [bulkEntryOpen, setBulkEntryOpen] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<Criterion | undefined>();
  const [deletingCriterionId, setDeletingCriterionId] = useState<string | null>(null);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);

  const handleAddCriterion = (criterion: Criterion) => {
    onUpdateGroup({
      ...group,
      criteria: [...group.criteria, criterion],
    });
  };

  const handleBulkAddCriteria = (criteria: Criterion[]) => {
    onUpdateGroup({
      ...group,
      criteria: [...group.criteria, ...criteria],
    });
  };

  const handleEditCriterion = (criterion: Criterion) => {
    onUpdateGroup({
      ...group,
      criteria: group.criteria.map((c) => (c.id === criterion.id ? criterion : c)),
    });
  };

  const handleDeleteCriterion = (criterionId: string) => {
    onUpdateGroup({
      ...group,
      criteria: group.criteria.filter((c) => c.id !== criterionId),
    });
    setDeletingCriterionId(null);
  };

  const openEditCriterion = (criterion: Criterion) => {
    setEditingCriterion(criterion);
    setCriterionFormOpen(true);
  };

  const openAddCriterion = () => {
    setEditingCriterion(undefined);
    setCriterionFormOpen(true);
  };

  const deletingCriterion = group.criteria.find((c) => c.id === deletingCriterionId);

  return (
    <>
      <Card data-testid={`criteria-group-card-${group.id}`}>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <CardTitle className="flex items-center gap-2 mb-2">
                <span className="truncate">{group.name}</span>
                <Badge variant="secondary" className="font-mono shrink-0">
                  {group.weight}%
                </Badge>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('criteria:groupCard.criteriaCount', { count: group.criteria.length })}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={onEditGroup}
                aria-label={t('common:actions.edit')}
              >
                <Edit className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteGroupDialogOpen(true)}
                aria-label={t('common:actions.delete')}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleExpand}
                aria-label={isExpanded ? t('common:actions.collapse') : t('common:actions.expand')}
                aria-expanded={isExpanded}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                )}
              </Button>
            </div>
          </div>
        </CardHeader>

        {isExpanded && (
          <CardContent className="space-y-3">
            {/* Criteria List */}
            {group.criteria.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium w-12">
                        {t('criteria:groupCard.table.importance')}
                      </th>
                      <th className="text-left p-2 font-medium min-w-[150px]">
                        {t('criteria:groupCard.table.title')}
                      </th>
                      <th className="text-left p-2 font-medium">
                        {t('criteria:groupCard.table.description')}
                      </th>
                      <th className="text-left p-2 font-medium w-[100px]">
                        {t('criteria:groupCard.table.category')}
                      </th>
                      <th className="text-center p-2 font-medium w-20">
                        {t('criteria:groupCard.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...group.criteria]
                      .sort((a, b) => {
                        // If both have categories, sort alphabetically
                        if (a.category && b.category) {
                          return a.category.localeCompare(b.category);
                        }
                        // If only a has category, it comes first
                        if (a.category && !b.category) return -1;
                        // If only b has category, it comes first
                        if (!a.category && b.category) return 1;
                        // If neither has category, maintain order
                        return 0;
                      })
                      .map((criterion) => (
                        <tr key={criterion.id} data-testid={`criterion-row-${criterion.id}`} className="border-b hover:bg-muted/50">
                          <td className="p-2 text-center">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="inline-flex">
                                    {importanceIcons[criterion.importance]}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{t(`criteria:form.importance.levels.${criterion.importance.toLowerCase()}`)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </td>
                          <td className="p-2 font-medium">
                            {criterion.title}
                          </td>
                          <td className="p-2 text-muted-foreground">
                            {criterion.description}
                          </td>
                          <td className="p-2">
                            {criterion.category && (
                              <Badge variant="secondary" className="text-xs">
                                {criterion.category}
                              </Badge>
                            )}
                          </td>
                          <td className="p-2">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditCriterion(criterion)}
                                aria-label={t('common:actions.edit')}
                                className="h-7 w-7 p-0"
                              >
                                <Edit className="h-3 w-3" aria-hidden="true" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeletingCriterionId(criterion.id)}
                                aria-label={t('common:actions.delete')}
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-3 w-3" aria-hidden="true" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">{t('criteria:groupCard.noCriteria')}</p>
              </div>
            )}

            {/* Add Criterion Buttons */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={openAddCriterion}
              >
                <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('criteria:groupCard.addCriterion')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkEntryOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" aria-hidden="true" />
                {t('criteria:groupCard.bulkAdd')}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Criterion Form Dialog */}
      <CriterionForm
        open={criterionFormOpen}
        onOpenChange={setCriterionFormOpen}
        onSave={editingCriterion ? handleEditCriterion : handleAddCriterion}
        criterion={editingCriterion}
        mode={editingCriterion ? 'edit' : 'add'}
      />

      {/* Bulk Criterion Entry Dialog */}
      <BulkCriterionEntry
        open={bulkEntryOpen}
        onOpenChange={setBulkEntryOpen}
        onSave={handleBulkAddCriteria}
      />

      {/* Delete Criterion Confirmation */}
      <AlertDialog
        open={deletingCriterionId !== null}
        onOpenChange={(open) => !open && setDeletingCriterionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('criteria:groupCard.deleteCriterion.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('criteria:groupCard.deleteCriterion.description', {
                name: deletingCriterion?.title,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingCriterionId && handleDeleteCriterion(deletingCriterionId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Group Confirmation */}
      <AlertDialog open={deleteGroupDialogOpen} onOpenChange={setDeleteGroupDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('criteria:groupCard.deleteGroup.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('criteria:groupCard.deleteGroup.description', {
                name: group.name,
                count: group.criteria.length,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDeleteGroup();
                setDeleteGroupDialogOpen(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
